// Карточка учётной записи площадки «Авито»: три вкладки — доступы, вакансии, журнал обмена.
// Вкладка живёт в адресе (?tab=vacancies): ссылка на вакансии записи открывается у коллеги
// вакансиями, а не доступами (правило 4).
//
// Вся карточка построена вокруг одного факта из тз 6.1: **наборов доступов ДВА, и они не
// взаимозаменяемы.** Переписка работает только ключом компании, вакансии ведутся ключом
// сотрудника — пакет размещения на площадке привязан к сотруднику, а не к компании. Поэтому у
// каждого ключа на вкладке «Доступы» написано словами, что именно перестанет работать без
// него: без этой фразы «ключ истёк» — это техническая новость, а не причина, по которой
// помощник молчит вторые сутки.
//
// Отдельного маршрута «карточка записи» в контракте нет: запись берётся из того же списка,
// что рисует левую колонку. Ключ кэша тот же, второго запроса к серверу не будет.

import { useState } from 'react';

import {
  RiDownloadCloud2Line,
  RiFileList3Line,
  RiRefreshLine,
  RiRobot2Line,
  RiSendPlaneLine,
  RiUpload2Line,
  RiKey2Line,
  RiAddLine,
  RiEdit2Line,
  RiExchangeLine,
} from '@remixicon/react';
import { Link, useNavigate } from 'react-router';

import { DetailPane } from '@/layout/DetailPane';
import { countLabel } from '@/shared/text/plural';
import { formatDateDMY, formatDateOnly, formatTimeHM, parseMoment } from '@/shared/time/format';
import * as Alert from '@/shared/ui/alert';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { CardTab, CardTabsList } from '@/shared/ui/card-tabs';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import * as LinkButton from '@/shared/ui/link-button';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import * as TabMenu from '@/shared/ui/tab-menu-horizontal';

import type {
  AccountOut,
  CredentialOut,
  ExchangeOut,
  KeyKind,
  PollResultOut,
  VacancyOut,
  VacancyState,
} from '../api/platform';
import { GateNote, useCan } from '../components/gates';
import { EXCHANGE_DIRECTION_TITLES, VACANCY_FORMS } from '../dictionaries';
import {
  useAccountsQuery,
  useBuildBulkUploadMutation,
  useExchangeLogQuery,
  usePollApplicationsMutation,
  usePublishVacancyMutation,
  useRefreshTokenMutation,
  useRefreshVacancyStatusMutation,
  useVacanciesQuery,
} from '../queries';
import type { AccountTab, HiringRoute } from '../route';
import {
  PANEL_ASSISTANT_SETTINGS,
  PANEL_BULK_EDIT,
  PANEL_CREDENTIALS,
  PANEL_NEW_VACANCY,
  accountTab,
  hiringHref,
  openPanelHref,
} from '../route';
import { MANAGE_OWNER, MANAGE_PERMISSION } from '../views/AccountsList';

/** Вакансии ведёт рекрутёр: у него пакет размещения (матрица тз, раздел 3.2). */
const PUBLISH_PERMISSION = 'hiring.avito.publish';
const PUBLISH_OWNER = 'Вакансии на площадке ведёт рекрутёр.';

/** Приём откликов заводит карточки кандидатов, поэтому прав нужно два сразу. */
const VIEW_PERMISSION = 'hiring.avito.view';
const CANDIDATE_EDIT_PERMISSION = 'hiring.candidate.edit';

const TAB_TITLES: Readonly<Record<AccountTab, string>> = {
  access: 'Доступы',
  vacancies: 'Вакансии',
  exchange: 'Журнал обмена',
};

/** Что именно перестанет работать без каждого набора. Ради этой пары фраз вкладка и нужна. */
const БЕЗ_КЛЮЧА: Readonly<Record<KeyKind, string>> = {
  company:
    'Без ключа компании не идёт переписка с соискателями: помощник не прочитает входящее сообщение и не отправит ответ. Вакансии при этом размещаются: для них годится и ключ сотрудника.',
  employee:
    'Без ключа сотрудника не разместить вакансию: пакет размещения на площадке привязан к сотруднику, а не к компании. Переписка при этом идёт: она работает ключом компании.',
};

/** Запасные подписи наборов на случай, если запись ещё не приехала: сервер даёт `kind_title`. */
const ЗАПАСНОЕ_ИМЯ_КЛЮЧА: Readonly<Record<KeyKind, string>> = {
  company: 'Ключ компании',
  employee: 'Ключ сотрудника',
};

const ЦВЕТ_СОСТОЯНИЯ: Readonly<Record<VacancyState, 'gray' | 'blue' | 'green' | 'red'>> = {
  draft: 'gray',
  publishing: 'blue',
  published: 'green',
  rejected: 'red',
  archived: 'gray',
};

/** Момент времени в предложении: «02.09.2026, 18:40». Машинный ISO человеку не показывается. */
function момент(value: string | null | undefined): string | null {
  const дата = parseMoment(value);
  return дата === null ? null : `${formatDateDMY(дата)}, ${formatTimeHM(дата)}`;
}

export function AccountDetail({ route }: { route: HiringRoute }) {
  if (route.rowId === undefined) {
    return (
      <DetailPane>
        <div className='flex h-full items-center justify-center'>
          {/* Ссылки-дубля на «подключить» здесь нет намеренно: она уже стоит в колонке слева,
              и два приглашения к одному действию человек читает как два разных действия. */}
          <EmptyState
            icon={RiKey2Line}
            title='Учётная запись не выбрана'
            description='Выберите запись в списке слева — здесь откроются её доступы (ключ компании для переписки и ключ сотрудника для вакансий), список вакансий с решениями проверки и журнал обмена с площадкой.'
          />
        </div>
      </DetailPane>
    );
  }
  return <Карточка accountId={route.rowId} route={route} />;
}

function Карточка({ accountId, route }: { accountId: string; route: HiringRoute }) {
  const navigate = useNavigate();
  const query = useAccountsQuery();
  const tab = accountTab(route.search.tab);
  const account = query.data?.items.find((item) => item.id === accountId);

  return (
    <TabMenu.Root
      value={tab}
      onValueChange={(next) =>
        navigate(hiringHref(route.view, { ...route.search, tab: next }, accountId))
      }
      className='flex h-full min-w-0 flex-1 flex-col'
    >
      <DetailPane
        title={
          <span className='flex min-w-0 items-center gap-2'>
            <RiKey2Line className='size-5 shrink-0 text-text-soft-400' />
            <span className='truncate'>{account?.title ?? 'Учётная запись площадки'}</span>
            {account !== undefined && !account.is_active && (
              <Badge.Root variant='lighter' color='orange' size='medium'>
                отключена
              </Badge.Root>
            )}
          </span>
        }
        actions={
          account === undefined ? undefined : (
            <Button.Root
              type='button'
              variant='neutral'
              mode='stroke'
              size='xsmall'
              onClick={() => navigate(openPanelHref(route, PANEL_ASSISTANT_SETTINGS, accountId))}
            >
              <Button.Icon as={RiRobot2Line} />
              Режим помощника
            </Button.Root>
          )
        }
        tabs={
          <CardTabsList>
            {(Object.keys(TAB_TITLES) as AccountTab[]).map((key) => (
              <CardTab key={key} value={key}>
                {TAB_TITLES[key]}
              </CardTab>
            ))}
          </CardTabsList>
        }
      >
        <ScreenState
          data={query.data}
          error={query.error}
          subject='учётные записи площадки «Авито»'
          isEmpty={() => account === undefined}
          onRetry={() => void query.refetch()}
          loading={
            <div className='flex flex-col gap-3 p-5'>
              <Skeleton className='h-32 w-full' />
              <Skeleton className='h-32 w-full' />
            </div>
          }
          empty={
            <div className='flex h-full items-center justify-center'>
              <EmptyState
                icon={RiKey2Line}
                title='Такой учётной записи в списке нет'
                description='Ссылка ведёт на запись, которой больше не видно: её могли отключить, либо она не входит в ваши права. Откройте список и выберите запись заново.'
                action={
                  <LinkButton.Root variant='primary' size='medium' underline asChild>
                    <Link to={hiringHref('accounts', { ...route.search, tab: undefined })}>
                      К списку учётных записей
                    </Link>
                  </LinkButton.Root>
                }
              />
            </div>
          }
        >
          {() =>
            account === undefined ? null : (
              <>
                <TabMenu.Content value='access'>
                  <ВкладкаДоступы account={account} route={route} />
                </TabMenu.Content>
                <TabMenu.Content value='vacancies'>
                  <ВкладкаВакансии account={account} route={route} />
                </TabMenu.Content>
                <TabMenu.Content value='exchange'>
                  <ВкладкаЖурнал account={account} />
                </TabMenu.Content>
              </>
            )
          }
        </ScreenState>
      </DetailPane>
    </TabMenu.Root>
  );
}

// ── Вкладка «Доступы» ─────────────────────────────────────────────────────────────────────

function ВкладкаДоступы({ account, route }: { account: AccountOut; route: HiringRoute }) {
  const refresh = useRefreshTokenMutation(account.id);

  return (
    <div className='flex flex-col gap-3 p-5'>
      {/* Строка о секрете стоит ОДИН раз на всю вкладку, а не по разу у каждого ключа: одна и
          та же причина, названная дважды, читается как два разных ограничения. Написать её
          обязательно — иначе человек ищет глазами поле «показать секрет», которого нет не по
          нашей скрытности, а потому что сервер секрет обратно не отдаёт вовсе. */}
      <p className='text-paragraph-sm text-text-sub-600'>
        Секрет доступа уходит на сервер зашифрованным и обратно не возвращается: посмотреть его
        негде ни здесь, ни где-либо ещё. Заменить — можно.
      </p>

      {account.credentials.map((набор) => (
        <БлокКлюча
          key={набор.kind}
          account={account}
          набор={набор}
          route={route}
          refresh={refresh}
        />
      ))}

      <БлокОткликов account={account} />
    </div>
  );
}

function БлокКлюча({
  account,
  набор,
  route,
  refresh,
}: {
  account: AccountOut;
  набор: CredentialOut;
  route: HiringRoute;
  refresh: ReturnType<typeof useRefreshTokenMutation>;
}) {
  const navigate = useNavigate();
  const canManage = useCan(MANAGE_PERMISSION);
  const [отказ, setОтказ] = useState<string | null>(null);

  const срок = момент(набор.token_valid_until);
  const обновлён = момент(набор.refreshed_at);
  const обновлений = набор.refresh_count ?? 0;
  // Мутация одна на обе половины вкладки: чей ответ пришёл, различает вид ключа в переменных.
  const этотКлюч = refresh.variables === набор.kind;
  const идёт = refresh.isPending && этотКлюч;

  function обновить() {
    if (!canManage) {
      setОтказ(`Права обновлять ключи площадки у вас нет. ${MANAGE_OWNER}`);
      return;
    }
    if (!набор.configured) {
      setОтказ(
        'Ключ выдаётся по набору доступов, а набора нет. Сначала заведите доступы кнопкой рядом.',
      );
      return;
    }
    if (refresh.isPending) {
      setОтказ('Ключ уже обновляется, ждём ответ площадки. Второй запрос не отправляем.');
      return;
    }
    setОтказ(null);
    refresh.mutate(набор.kind);
  }

  function заменить() {
    if (!canManage) {
      setОтказ(`Права заводить доступы площадки у вас нет. ${MANAGE_OWNER}`);
      return;
    }
    setОтказ(null);
    navigate(openPanelHref(route, PANEL_CREDENTIALS, account.id));
  }

  return (
    <section className='flex flex-col gap-2'>
      <h3 className='text-label-sm text-text-strong-950'>
        {набор.kind_title || ЗАПАСНОЕ_ИМЯ_КЛЮЧА[набор.kind]}
      </h3>
      <p className='text-paragraph-sm text-text-sub-600'>{БЕЗ_КЛЮЧА[набор.kind]}</p>

      <FieldList>
        <FieldRow
          label='Набор доступов'
          value={набор.configured ? 'заведён' : null}
          missing='не заведён'
        />
        {/* Три разных факта тремя разными видами (правило 7): набора нет вовсе — это не то же
            самое, что «набор есть, а ключ ещё не выпускали», и не то же, что «ключ протух».
            Заведение доступов ключа НЕ выдаёт: он выпускается сам при первом обращении. */}
        <FieldRow
          label='Ключ доступа'
          value={
            !набор.configured
              ? null
              : срок === null
                ? null
                : набор.token_alive
                  ? `действует до ${срок}`
                  : `истёк ${срок}`
          }
          missing={
            !набор.configured
              ? 'ключа нет: он выдаётся по набору доступов'
              : 'ещё ни разу не выдавался'
          }
          note={
            !набор.configured
              ? undefined
              : срок === null
                ? 'ключ выпускается сам при первом обращении к площадке и живёт сутки'
                : набор.token_alive
                  ? 'обновляется сам за пять минут до срока — обмен из-за этого не прерывается'
                  : 'следующее обращение к площадке выпустит новый; можно выпустить и сейчас'
          }
        />
        <FieldRow
          label='Ключ выпускали заново'
          value={
            !набор.configured
              ? null
              : обновлений === 0
                ? 'ни разу'
                : countLabel(обновлений, ['раз', 'раза', 'раз'])
          }
          missing='выпускать нечего: набора доступов нет'
          note={обновлён === null ? undefined : `последний раз ${обновлён}`}
        />
      </FieldList>

      <div className='flex flex-wrap items-center gap-2'>
        <Button.Root type='button' variant='neutral' mode='stroke' size='xsmall' onClick={обновить}>
          <Button.Icon as={RiRefreshLine} />
          {идёт ? 'Обновляем ключ…' : 'Обновить ключ'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='xsmall' onClick={заменить}>
          <Button.Icon as={RiKey2Line} />
          {набор.configured ? 'Заменить доступы' : 'Завести доступы'}
        </Button.Root>
      </div>

      {отказ !== null && <GateNote>{отказ}</GateNote>}
      {refresh.isError && этотКлюч && <ErrorAlert error={refresh.error} />}
      {refresh.isSuccess && этотКлюч && (
        // Новый срок уже стоит строкой выше, и повторять его тут незачем. Подтверждение нужно
        // ради другого: сказать, что смена ключа не рвёт текущую работу с площадкой.
        <Alert.Root variant='light' status='success' size='small' role='status'>
          <Alert.Icon as={RiKey2Line} />
          <span>Ключ выпущен заново. Обмен с площадкой при этом не прерывается.</span>
        </Alert.Root>
      )}
    </section>
  );
}

function БлокОткликов({ account }: { account: AccountOut }) {
  const canPlatform = useCan(VIEW_PERMISSION);
  const canCandidates = useCan(CANDIDATE_EDIT_PERMISSION);
  const mutation = usePollApplicationsMutation(account.id);
  const [отказ, setОтказ] = useState<string | null>(null);

  function забрать() {
    if (!canPlatform || !canCandidates) {
      setОтказ(
        'Забирать отклики может тот, у кого есть и доступ к площадке, и право заводить карточки кандидатов: каждый отклик превращается в карточку. Оба права есть у рекрутёра.',
      );
      return;
    }
    if (!account.can_publish) {
      setОтказ(
        'Доступов к площадке у этой записи нет, обращаться нечем. Заведите набор доступов выше.',
      );
      return;
    }
    if (mutation.isPending) {
      setОтказ('Отклики уже забираются, ждём ответ площадки. Второй запрос не отправляем.');
      return;
    }
    setОтказ(null);
    mutation.mutate();
  }

  return (
    <section className='flex flex-col gap-2'>
      <h3 className='text-label-sm text-text-strong-950'>Отклики соискателей</h3>
      <p className='text-paragraph-sm text-text-sub-600'>
        Обычно отклик приезжает сам: площадка сообщает о каждом, и карточка кандидата заводится без
        участия человека. Эта кнопка — запасной путь на случай, когда сообщение не дошло.
      </p>

      <div className='flex flex-wrap items-center gap-2'>
        <Button.Root type='button' variant='neutral' mode='stroke' size='xsmall' onClick={забрать}>
          <Button.Icon as={RiDownloadCloud2Line} />
          {mutation.isPending ? 'Забираем отклики…' : 'Забрать отклики с площадки'}
        </Button.Root>
      </div>

      {отказ !== null && <GateNote>{отказ}</GateNote>}
      {mutation.isError && <ErrorAlert error={mutation.error} />}
      {mutation.isSuccess && (
        <Alert.Root variant='light' status='success' size='small' role='status'>
          <Alert.Icon as={RiDownloadCloud2Line} />
          <span>{итогПриёма(mutation.data)}</span>
        </Alert.Root>
      )}
    </section>
  );
}

/**
 * Итог приёма откликов фразой, а не рядом чисел.
 *
 * Ряд равнозначных величин человек читает как формулу и сверяет её в уме; здесь сходится
 * «взяли = завели + повторы», но ради трёх чисел разворачивать полосу величин незачем. А ноль
 * обязан выглядеть нулём с причиной, а не пустотой (правило 7): «забирать было нечего» и
 * «запрос не прошёл» — это разные новости.
 */
function итогПриёма(итог: PollResultOut): string {
  if (итог.fetched === 0) {
    return 'Площадка не отдала ни одного отклика: новых с прошлого раза не было.';
  }
  const взяли = countLabel(итог.fetched, ['отклик', 'отклика', 'откликов']);
  if (итог.candidates_created === 0) {
    return `Забрали ${взяли}, и все они от людей, которые уже есть в воронке: новых карточек не появилось.`;
  }
  const завели = countLabel(итог.candidates_created, ['карточку', 'карточки', 'карточек']);
  if (итог.repeat_applications === 0) {
    return `Забрали ${взяли}, завели ${завели} кандидатов.`;
  }
  return `Забрали ${взяли}: завели ${завели} кандидатов, остальные ${итог.repeat_applications} — повторные отклики от тех, кто уже в воронке.`;
}

// ── Вкладка «Вакансии» ────────────────────────────────────────────────────────────────────

function ВкладкаВакансии({ account, route }: { account: AccountOut; route: HiringRoute }) {
  const navigate = useNavigate();
  const canPublish = useCan(PUBLISH_PERMISSION);
  const query = useVacanciesQuery({ account_id: account.id });
  const publish = usePublishVacancyMutation();
  const refreshStatus = useRefreshVacancyStatusMutation();
  const bulk = useBuildBulkUploadMutation();
  const [отказ, setОтказ] = useState<string | null>(null);

  function черновик() {
    if (!canPublish) {
      setОтказ(`Права вести вакансии на площадке у вас нет. ${PUBLISH_OWNER}`);
      return;
    }
    setОтказ(null);
    navigate(openPanelHref(route, PANEL_NEW_VACANCY, account.id));
  }

  function собрать() {
    if (!canPublish) {
      setОтказ(`Права собирать выгрузку вакансий у вас нет. ${PUBLISH_OWNER}`);
      return;
    }
    if (bulk.isPending) {
      setОтказ('Файл выгрузки уже собирается. Второй запрос не отправляем.');
      return;
    }
    setОтказ(null);
    bulk.mutate(account.id);
  }

  function изменитьУВсех() {
    if (!canPublish) {
      setОтказ(`Права менять вакансии на площадке у вас нет. ${PUBLISH_OWNER}`);
      return;
    }
    setОтказ(null);
    navigate(openPanelHref(route, PANEL_BULK_EDIT, account.id));
  }

  const кнопкаЧерновика = (
    <Button.Root type='button' variant='primary' mode='stroke' size='xsmall' onClick={черновик}>
      <Button.Icon as={RiAddLine} />
      Черновик вакансии
    </Button.Root>
  );

  return (
    <div className='flex flex-col gap-3 p-5'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        {/* Правило 2: над списком написано, что перечислено и сколько. Столбик карточек без
            числа сверху человек читает как «вот всё», не зная, всё ли доехало. */}
        <h3 className='text-label-sm text-text-strong-950'>
          {query.data === undefined
            ? 'Вакансии записи'
            : countLabel(query.data.total, VACANCY_FORMS)}
        </h3>
        <div className='flex flex-wrap items-center gap-2'>
          {/* «Изменить у всех» стоит рядом с выгрузкой, а не в строке объявления: действие
              над МНОЖЕСТВОМ живёт над списком, а не внутри строки (правило 3). */}
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={изменитьУВсех}
          >
            <Button.Icon as={RiEdit2Line} />
            Изменить у всех
          </Button.Root>
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={собрать}
          >
            <Button.Icon as={RiUpload2Line} />
            {bulk.isPending ? 'Собираем файл…' : 'Собрать файл выгрузки'}
          </Button.Root>
          {кнопкаЧерновика}
        </div>
      </div>

      {отказ !== null && <GateNote>{отказ}</GateNote>}
      {bulk.isError && <ErrorAlert error={bulk.error} />}
      {bulk.isSuccess && (
        <Alert.Root variant='light' status='information' size='small' role='status'>
          <Alert.Icon as={RiUpload2Line} />
          <div className='flex min-w-0 flex-col gap-0.5'>
            <span>
              {bulk.data.vacancy_count === 0
                ? 'Файл собран пустым: у записи нет черновиков, которые в него попали бы.'
                : `Файл собран, в него попало ${countLabel(bulk.data.vacancy_count, VACANCY_FORMS)}.`}
            </span>
            <span className='text-paragraph-xs opacity-80'>
              Адрес файла для автозагрузки на стороне площадки:
            </span>
            <span className='break-all font-mono text-paragraph-xs'>{bulk.data.feed_url}</span>
          </div>
        </Alert.Root>
      )}

      <ScreenState
        data={query.data}
        error={query.error}
        subject='вакансии учётной записи'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={
          <div className='flex flex-col gap-3'>
            <Skeleton className='h-28 w-full' />
            <Skeleton className='h-28 w-full' />
          </div>
        }
        empty={
          <EmptyState
            icon={RiFileList3Line}
            title='У этой записи нет ни одной вакансии'
            description='Ни черновиков, ни размещённых объявлений. Заведите черновик — из него объявление уходит на площадку одним нажатием, а решение проверки приезжает позже и видно здесь же.'
            action={кнопкаЧерновика}
          />
        }
      >
        {(data) => (
          <div className='flex flex-col gap-4'>
            {data.items.map((вакансия) => (
              <КарточкаВакансии
                key={вакансия.id}
                вакансия={вакансия}
                account={account}
                canPublish={canPublish}
                publish={publish}
                refreshStatus={refreshStatus}
              />
            ))}
          </div>
        )}
      </ScreenState>
    </div>
  );
}

function КарточкаВакансии({
  вакансия,
  account,
  canPublish,
  publish,
  refreshStatus,
}: {
  вакансия: VacancyOut;
  account: AccountOut;
  canPublish: boolean;
  publish: ReturnType<typeof usePublishVacancyMutation>;
  refreshStatus: ReturnType<typeof useRefreshVacancyStatusMutation>;
}) {
  const [отказ, setОтказ] = useState<string | null>(null);
  const этаРазмещается = publish.variables === вакансия.id;
  const этаОбновляется = refreshStatus.variables === вакансия.id;

  // Название набора берётся у сервера (`kind_title`), а не пишется здесь второй раз: копия
  // названия рядом с присланным расходится с ним молча.
  const имяКлюча =
    account.credentials.find((набор) => набор.kind === вакансия.key_kind)?.kind_title ??
    ЗАПАСНОЕ_ИМЯ_КЛЮЧА[вакансия.key_kind];

  function разместить() {
    if (!canPublish) {
      setОтказ(`Права размещать вакансии у вас нет. ${PUBLISH_OWNER}`);
      return;
    }
    if (publish.isPending) {
      setОтказ('Объявление уже уходит на площадку, ждём ответ. Второй запрос не отправляем.');
      return;
    }
    setОтказ(null);
    publish.mutate(вакансия.id);
  }

  function обновить() {
    if (!canPublish) {
      setОтказ(`Права запрашивать статусы у площадки у вас нет. ${PUBLISH_OWNER}`);
      return;
    }
    if (refreshStatus.isPending) {
      setОтказ('Статусы уже запрашиваются у площадки. Второй запрос не отправляем.');
      return;
    }
    setОтказ(null);
    refreshStatus.mutate(вакансия.id);
  }

  return (
    <section className='flex flex-col gap-2'>
      <div className='flex min-w-0 items-center gap-2'>
        <span className='min-w-0 flex-1 truncate text-label-sm text-text-strong-950'>
          {вакансия.title}
        </span>
        {/* Состояние стоит меткой и в полях НЕ повторяется. Метка отвечает на «что с нашей
            стороны», а поле ниже — на «что решила площадка»: сценарий 105 требует, чтобы эти
            два факта, приходящие в разное время, были видны порознь. */}
        <Badge.Root
          variant='lighter'
          color={ЦВЕТ_СОСТОЯНИЯ[вакансия.state] ?? 'gray'}
          size='medium'
        >
          {вакансия.state_title}
        </Badge.Root>
      </div>

      <FieldList>
        <FieldRow
          label='Город'
          value={вакансия.city}
          note={вакансия.region === null ? undefined : `регион: ${вакансия.region}`}
        />
        <FieldRow
          label='Место работы'
          value={вакансия.workplace}
          missing='не указано'
          note='без места работы площадка не пропускает один и тот же текст в двух городах'
        />
        <FieldRow
          label='Каким ключом ведётся'
          value={имяКлюча}
          note={
            вакансия.key_kind === 'employee'
              ? 'пакет размещения на площадке привязан к сотруднику, а не к компании'
              : 'ключом компании вакансии тоже размещаются, но пакет размещения не у него'
          }
        />
        <FieldRow
          label='Решение проверки на площадке'
          value={вакансия.moderation_status_title}
          missing={ждёмПроверку(вакансия)}
          note={вакансия.moderation_note ?? undefined}
        />
        <FieldRow
          label='Размещена'
          value={момент(вакансия.published_at)}
          missing='на площадку ещё не уходила'
          note={
            вакансия.external_id === null
              ? undefined
              : `номер объявления на площадке: ${вакансия.external_id}`
          }
        />
      </FieldList>

      <div className='flex flex-wrap items-center gap-2'>
        {вакансия.state === 'draft' && (
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={разместить}
          >
            <Button.Icon as={RiSendPlaneLine} />
            {publish.isPending && этаРазмещается ? 'Отправляем…' : 'Разместить'}
          </Button.Root>
        )}
        {вакансия.external_id !== null && (
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={обновить}
          >
            <Button.Icon as={RiRefreshLine} />
            {refreshStatus.isPending && этаОбновляется
              ? 'Спрашиваем площадку…'
              : 'Обновить статусы'}
          </Button.Root>
        )}
      </div>

      {отказ !== null && <GateNote>{отказ}</GateNote>}
      {/* Сценарий 107 — запрет одинакового объявления в одном городе со второй записи — живёт
          на сервере: отдельного маршрута предпроверки в контракте нет, отказ приезжает при
          размещении. Конверт сервера уже несёт и причину («такое же объявление стоит под
          записью N»), и следующий шаг, поэтому свой текст поверх него не нужен и вреден. */}
      {publish.isError && этаРазмещается && <ErrorAlert error={publish.error} />}
      {refreshStatus.isError && этаОбновляется && <ErrorAlert error={refreshStatus.error} />}
    </section>
  );
}

/** Почему решения проверки ещё нет. Пустота без причины читается как «данные не доехали». */
function ждёмПроверку(вакансия: VacancyOut): string {
  if (вакансия.state === 'draft') {
    return 'черновик на площадку не уходил — проверять пока нечего';
  }
  if (вакансия.external_id === null) {
    return 'объявление уходит на площадку, номера от неё ещё нет';
  }
  return 'площадка объявление приняла, решение проверки придёт позже';
}

// ── Вкладка «Журнал обмена» ───────────────────────────────────────────────────────────────

function ВкладкаЖурнал({ account }: { account: AccountOut }) {
  const query = useExchangeLogQuery(account.id);

  return (
    <div className='flex flex-col gap-3 p-5'>
      <p className='text-paragraph-sm text-text-sub-600'>
        Что и когда уходило на площадку и чем закончилось. Персональных данных здесь нет: ни имён
        соискателей, ни телефонов, ни текстов переписки — только время, вид ключа и ответ площадки.
      </p>

      <ScreenState
        data={query.data}
        error={query.error}
        subject='журнал обмена с площадкой'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={
          <div className='flex flex-col gap-2'>
            <Skeleton className='h-12 w-full' />
            <Skeleton className='h-12 w-full' />
            <Skeleton className='h-12 w-full' />
          </div>
        }
        empty={
          <EmptyState
            icon={RiExchangeLine}
            title='Обмена с площадкой ещё не было'
            illustration='обмен'
            description='Записью пока не пользовались: ни ключ не выпускали, ни объявление не отправляли, ни отклик не забирали. Первая же строка появится здесь сама.'
          />
        }
      >
        {(data) => (
          <div className='flex flex-col'>
            {data.items.map((строка) => (
              <СтрокаОбмена key={строка.id} строка={строка} account={account} />
            ))}
            {data.items.length < data.total && (
              <p className='pt-2 text-paragraph-xs text-text-sub-600'>
                Показаны последние {data.items.length} обращений из {data.total}.
              </p>
            )}
          </div>
        )}
      </ScreenState>
    </div>
  );
}

/** Чем кончилось обращение. Четыре разных исхода — четыре разных вида (правило 7). */
function исходОбмена(строка: ExchangeOut): {
  подпись: string;
  цвет: 'green' | 'red' | 'gray';
} {
  if (строка.error_code !== null) {
    return { подпись: 'ошибка', цвет: 'red' };
  }
  if (строка.http_status === null) {
    return { подпись: 'ответа не было', цвет: 'gray' };
  }
  if (строка.http_status >= 400) {
    return { подпись: `отказ ${строка.http_status}`, цвет: 'red' };
  }
  return { подпись: 'успех', цвет: 'green' };
}

function СтрокаОбмена({ строка, account }: { строка: ExchangeOut; account: AccountOut }) {
  const исход = исходОбмена(строка);
  const когда = момент(строка.created_at) ?? formatDateOnly(строка.created_at);
  const ключ =
    строка.kind === null
      ? 'без ключа'
      : (account.credentials.find((набор) => набор.kind === строка.kind)?.kind_title ??
        ЗАПАСНОЕ_ИМЯ_КЛЮЧА[строка.kind]);

  const подробности = [
    `операция ${строка.operation}`,
    ключ.toLowerCase(),
    строка.http_status === null ? 'кода ответа нет' : `код ответа ${строка.http_status}`,
    строка.duration_ms === null ? 'время не замерено' : `за ${строка.duration_ms} мс`,
  ].join(' · ');

  return (
    <div className='flex flex-col gap-0.5 border-b border-stroke-soft-200 py-2 last:border-b-0'>
      <div className='flex min-w-0 items-center gap-2'>
        <span className='shrink-0 tabular-nums text-paragraph-xs text-text-sub-600'>{когда}</span>
        <span className='min-w-0 flex-1 truncate text-label-sm text-text-strong-950'>
          {EXCHANGE_DIRECTION_TITLES[строка.direction]}
        </span>
        <Badge.Root variant='lighter' color={исход.цвет} size='medium'>
          {исход.подпись}
        </Badge.Root>
      </div>
      <span className='text-paragraph-xs text-text-sub-600'>{подробности}</span>
      {/* Строка с ошибкой обязана читаться сразу: журнал — это доказательство сценария 110
          («подмена доступа даёт понятную ошибку») и 111 («ключ обновился, обмен не прервался»).
          Код ошибки уходит вниз отдельной красной строкой, а не прячется в общий перечень. */}
      {строка.error_code !== null && (
        <span className='text-paragraph-xs text-error-base'>код ошибки: {строка.error_code}</span>
      )}
    </div>
  );
}
